import { Routes, Route, BrowserRouter as Router } from 'react-router-dom';

import RootLayout from './layouts/RootLayout';
import MainLayout from './layouts/MainLayout';
import LoginLayout from './layouts/LoginLayout';
import ChatLayout from './layouts/ChatLayout';
import AuthenticatedProductRuntime from './layouts/AuthenticatedProductRuntime';
import RequireAuthenticatedSession from './layouts/RequireAuthenticatedSession';

import LoginScreen from './screens/LoginScreen';

import ProfileEditScreen from "./screens/profile/ProfileEditScreen";
import ProfileScreen from './screens/profile/ProfileScreen';
import ProfileEmailValidateScreen from "./screens/profile/ProfileEmailValidateScreen";
import ProfileChangePasswordScreen from "./screens/profile/ProfileChangePasswordScreen";

import UserListScreen from "./screens/user/UserListScreen";
import UserScreen from "./screens/user/UserScreen";

import NotificationListScreen from "./screens/notifications/NotificationListScreen";
import NotificationPreferencesScreen from "./screens/notifications/NotificationPreferencesScreen";
import CompanyContactEditScreen from "./screens/companyContact/CompanyContactEditScreen";
import CompanyFinderScreen from "./screens/companyFinder/CompanyFinderScreen";
import CompanyJoinRequestScreen from "./screens/companyJoinRequest/CompanyJoinRequestScreen";
import CompanyJoinRequestWithClientScreen from "./screens/companyJoinRequestWithClient/CompanyJoinRequestWithClientScreen";
import CompanyInvitationCreateScreen from "./screens/companyInvitations/CompanyInvitationCreateScreen";
import CompanyMineScreen from "./screens/companyMine/CompanyMineScreen";
import CompanyNewScreen from "./screens/companyNew/CompanyNewScreen";
import CompanyProfileScreen from "./screens/companyProfile/CompanyProfileScreen";
import CompanySettingsScreen from "./screens/companySettings/CompanySettingsScreen";
import CompanyMembersScreen from "./screens/companyMembers/CompanyMembersScreen";
import CompanyInvitationsManageScreen from "./screens/companyInvitationsManage/CompanyInvitationsManageScreen";
import CompanyJoinRequestsManageScreen from "./screens/companyJoinRequestsManage/CompanyJoinRequestsManageScreen";
import CompanyMyInvitationsScreen from "./screens/companyMyInvitations/CompanyMyInvitationsScreen";
import CompanyMyJoinRequestsScreen from "./screens/companyMyJoinRequests/CompanyMyJoinRequestsScreen";
import CompanyInvitationByTokenScreen from "./screens/companyInvitationByToken/CompanyInvitationByTokenScreen";
import ClientContactsEditScreen from "./screens/clientContacts/ClientContactsEditScreen";
import ClientEditScreen from "./screens/clientEdit/ClientEditScreen";
import ClientActivityScreen from "./screens/clientActivity/ClientActivityScreen";
import ClientsListScreen from "./screens/clientsList/ClientsListScreen";
import ClientDetailScreen from "./screens/clientDetail/ClientDetailScreen";
import CompanyClientsListScreen from "./screens/companyClients/CompanyClientsListScreen";
import CompanyClientsUnassignedScreen from "./screens/companyClients/CompanyClientsUnassignedScreen";
import CompanyClientDetailScreen from "./screens/companyClients/CompanyClientDetailScreen";
import CompanyClientImportScreen from "./screens/companyClients/CompanyClientImportScreen";
import CompanyPropertiesListScreen from "./screens/companyProperties/CompanyPropertiesListScreen";
import CompanyPropertiesUnassignedScreen from "./screens/companyProperties/CompanyPropertiesUnassignedScreen";
import CompanyPropertyDetailScreen from "./screens/companyProperties/CompanyPropertyDetailScreen";
import CompanyPropertyCreateScreen from "./screens/companyProperties/CompanyPropertyCreateScreen";
import CompanyPropertyEditScreen from "./screens/companyProperties/CompanyPropertyEditScreen";
import CompanyPropertyImportScreen from "./screens/companyProperties/CompanyPropertyImportScreen";
import CompanyWorkListScreen from "./screens/companyWork/CompanyWorkListScreen";
import CompanyWorkCalendarScreen from "./screens/companyWork/CompanyWorkCalendarScreen";
import CompanyTaskDetailScreen from "./screens/companyWork/CompanyTaskDetailScreen";
import CompanyViewingDetailScreen from "./screens/companyWork/CompanyViewingDetailScreen";
import DealsListScreen from "./screens/dealsList/DealsListScreen";
import DealDetailScreen from "./screens/dealDetail/DealDetailScreen";
import FinanceHubScreen from "./screens/financeHub/FinanceHubScreen";
import CompanyFinanceScreen from "./screens/companyFinance/CompanyFinanceScreen";
import CompanyMyRemunerationScreen from "./screens/companyMyRemuneration/CompanyMyRemunerationScreen";
import CompanyOverviewScreen from "./screens/companyOverview/CompanyOverviewScreen";
import TasksListScreen from "./screens/tasksList/TasksListScreen";
import TasksCalendarScreen from "./screens/tasksCalendar/TasksCalendarScreen";
import DashboardScreen from "./screens/dashboard/DashboardScreen";
import BillingScreen from "./screens/billing/BillingScreen";
import PaymentResultScreen from "./screens/payment/PaymentResultScreen";

import RealtySearchScreen from "./screens/realtySearch/RealtySearchScreen";
import RealtyMapScreen from "./screens/realtyMap/RealtyMapScreen";
import RealtyHomeScreen from "./screens/realtyHome/RealtyHomeScreen";
import RealtySavedScreen from "./screens/realtySaved/RealtySavedScreen";
import RealtyDetailScreen from "./screens/realtyDetail/RealtyDetailScreen";
import RealtyEditScreen from "./screens/realtyEdit/RealtyEditScreen";
import RealtyCreateScreen from "./screens/realtyCreate/RealtyCreateScreen";

import ChatsLayout from "./screens/chat/ChatsLayout";
import NotFoundScreen from "./screens/notFound/NotFoundScreen";
import PublicPresentationScreen from "./screens/publicPresentation/PublicPresentationScreen";
import { isCompanyWorkspaceEnabled } from "./config/companyWorkspace";
import { isCompanyFinanceEnabled } from "./config/companyFinance";
import { isCompanyOverviewEnabled } from "./config/companyOverview";


function App() {
  const companyWorkspaceEnabled = isCompanyWorkspaceEnabled();
  const companyFinanceEnabled = isCompanyFinanceEnabled();
  const companyOverviewEnabled = isCompanyOverviewEnabled();

  return (
    <Router>
      <Routes>
        <Route path="/p/:token" element={<PublicPresentationScreen />} />

        {/* Public login: theme/toast shell only — no bootstrap/WS product runtime. */}
        <Route element={<RootLayout />}>
          <Route element={<LoginLayout />}>
            <Route path="/login" element={<LoginScreen />} />
          </Route>
        </Route>

        <Route element={<AuthenticatedProductRuntime />}>
          <Route element={<RequireAuthenticatedSession />}>
            <Route element={<RootLayout />}>

              {/* Authenticated onboarding: runtime/WS allowed, no MainLayout nav. */}
              <Route element={<LoginLayout />}>
                {/* AUTH-ONBOARDING-EMAIL-VERIFICATION-A1: keep URL; place outside
                    MainLayout so unverified users cannot browse product chrome. */}
                <Route
                  path="/profile/email-verification"
                  element={<ProfileEmailValidateScreen />}
                />
              </Route>

            {/* CHAT FULLSCREEN */}
            <Route element={<ChatLayout />}>
              <Route path="/chats/:id" element={<ChatsLayout />} />
            </Route>

            {/* MAIN */}
            <Route element={<MainLayout />}>
              {/* Routing during R2 stabilization:
                  /         → ProfileScreen   (unchanged anchor for runtime safety)
                  /profile  → ProfileScreen   (future canonical profile route — added in R2)
                  /feed     → RealtyHomeScreen (temporary realty home feed; will own / later)
                  /search   → RealtySearchScreen
                  /saved    → RealtySavedScreen
                  Per R2 routing decision: do NOT overwrite / during R2. */}
              <Route path="/" element={<ProfileScreen />} />
              <Route path="/profile" element={<ProfileScreen />} />

              <Route path="/feed" element={<RealtyHomeScreen />} />
              <Route path="/search" element={<RealtySearchScreen />} />
              <Route path="/map" element={<RealtyMapScreen />} />
              <Route path="/saved" element={<RealtySavedScreen />} />
              <Route path="/realty/:id" element={<RealtyDetailScreen />} />
              <Route path="/realty/:id/edit" element={<RealtyEditScreen />} />
              <Route path="/add" element={<RealtyCreateScreen />} />

              <Route path="/profile/edit" element={<ProfileEditScreen />} />
              <Route path="/profile/change-password" element={<ProfileChangePasswordScreen />} />
              <Route path="/profile/billing" element={<BillingScreen />} />
              <Route path="/payment/result" element={<PaymentResultScreen />} />

              <Route path="/users" element={<UserListScreen />} />
              <Route path="/user/:id" element={<UserScreen />} />

              <Route path="/dashboard" element={<DashboardScreen />} />

              <Route path="/notifications" element={<NotificationListScreen />} />
              <Route path="/notifications/preferences" element={<NotificationPreferencesScreen />} />

              {companyWorkspaceEnabled ? (
                <>
                  <Route path="/companies/mine" element={<CompanyMineScreen />} />
                  <Route path="/companies/new" element={<CompanyNewScreen />} />
                  <Route path="/companies/find" element={<CompanyFinderScreen />} />
                  <Route path="/companies/join-request" element={<CompanyJoinRequestScreen />} />
                  <Route path="/companies/join-request/new-client" element={<CompanyJoinRequestWithClientScreen />} />
                  <Route path="/companies/my-invitations" element={<CompanyMyInvitationsScreen />} />
                  <Route path="/companies/my-join-requests" element={<CompanyMyJoinRequestsScreen />} />
                  <Route path="/companies/invitations/by-token/:token" element={<CompanyInvitationByTokenScreen />} />
                  <Route path="/companies/:companyId/contact/edit" element={<CompanyContactEditScreen />} />
                  <Route path="/companies/:companyId/invitations/new" element={<CompanyInvitationCreateScreen />} />
                  <Route path="/companies/:companyId/settings" element={<CompanySettingsScreen />} />
                  <Route path="/companies/:companyId/members" element={<CompanyMembersScreen />} />
                  <Route path="/companies/:companyId/invitations" element={<CompanyInvitationsManageScreen />} />
                  <Route path="/companies/:companyId/join-requests" element={<CompanyJoinRequestsManageScreen />} />
                  <Route path="/companies/:companyId/clients/unassigned" element={<CompanyClientsUnassignedScreen />} />
                  <Route path="/companies/:companyId/clients/import" element={<CompanyClientImportScreen />} />
                  <Route path="/companies/:companyId/clients/:clientId" element={<CompanyClientDetailScreen />} />
                  <Route path="/companies/:companyId/clients" element={<CompanyClientsListScreen />} />
                  <Route path="/companies/:companyId/properties/unassigned" element={<CompanyPropertiesUnassignedScreen />} />
                  <Route path="/companies/:companyId/properties/new" element={<CompanyPropertyCreateScreen />} />
                  <Route path="/companies/:companyId/properties/import" element={<CompanyPropertyImportScreen />} />
                  <Route path="/companies/:companyId/properties/:propertyId/edit" element={<CompanyPropertyEditScreen />} />
                  <Route path="/companies/:companyId/properties/:propertyId" element={<CompanyPropertyDetailScreen />} />
                  <Route path="/companies/:companyId/properties" element={<CompanyPropertiesListScreen />} />
                  <Route path="/companies/:companyId/tasks/calendar" element={<CompanyWorkCalendarScreen />} />
                  <Route path="/companies/:companyId/tasks/:taskId" element={<CompanyTaskDetailScreen />} />
                  <Route path="/companies/:companyId/tasks" element={<CompanyWorkListScreen />} />
                  <Route path="/companies/:companyId/viewings/:viewingId" element={<CompanyViewingDetailScreen />} />
                  {companyFinanceEnabled ? (
                    <>
                      <Route path="/companies/:companyId/finance" element={<CompanyFinanceScreen />} />
                      <Route path="/companies/:companyId/my-remuneration" element={<CompanyMyRemunerationScreen />} />
                    </>
                  ) : null}
                  {companyOverviewEnabled ? (
                    <Route path="/companies/:companyId/overview" element={<CompanyOverviewScreen />} />
                  ) : null}
                  <Route path="/companies/:companyId" element={<CompanyProfileScreen />} />
                </>
              ) : null}

              <Route path="/clients" element={<ClientsListScreen />} />
              <Route path="/clients/:clientId" element={<ClientDetailScreen />} />
              <Route path="/clients/:clientId/edit" element={<ClientEditScreen />} />
              <Route path="/clients/:clientId/contacts/edit" element={<ClientContactsEditScreen />} />
              <Route path="/clients/:clientId/activity" element={<ClientActivityScreen />} />

              <Route path="/deals" element={<DealsListScreen />} />
              <Route path="/deals/:dealId" element={<DealDetailScreen />} />
              <Route path="/finance" element={<FinanceHubScreen />} />

              <Route path="/tasks" element={<TasksListScreen />} />
              <Route path="/tasks/calendar" element={<TasksCalendarScreen />} />

              <Route path="/chats" element={<ChatsLayout />} />
            </Route>

            {/* ROUTE-INTEGRITY-404-A1: lowest-priority unmatched client routes.
                Sibling under RootLayout (not MainLayout) so unknown URLs do not
                mount product Navbar chrome. Does not shadow canonical routes. */}
              <Route path="*" element={<NotFoundScreen />} />

            </Route>
          </Route>
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
